import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal } from 'lucide-react';
import { useContacts } from '@/composables/crm/useContacts';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { formatDate, formatFullName } from '@/lib/formatters';
import { useState, useMemo } from 'react';
import type { Contact } from '@/types/table';

export function ContactListPage() {
  const navigate = useNavigate();
  const { contacts, meta, isLoading, filters, exportCsv, deleteContact } = useContacts();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [deleteDeals, setDeleteDeals] = useState(false);
  const [deleteLead, setDeleteLead] = useState(false);

  const columns: ColumnDef<Contact, unknown>[] = useMemo(() => [
    { accessorKey: 'name', header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />, cell: ({ row }) => <div><p className="font-medium">{formatFullName(row.original.firstName, row.original.lastName)}</p><p className="text-xs text-muted-foreground">{row.original.email}</p></div> },
    { accessorKey: 'company', header: 'Company', cell: ({ row }) => row.original.company?.name || '-' },
    { accessorKey: 'jobTitle', header: 'Job Title', cell: ({ row }) => row.original.jobTitle || '-' },
    { accessorKey: 'phone', header: 'Phone', cell: ({ row }) => row.original.phone || '-' },
    { accessorKey: 'createdAt', header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />, cell: ({ row }) => formatDate(row.original.createdAt) },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${row.original.id}`); }}>View</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer title="Contacts" description="Manage your contacts" actions={<><Button variant="outline" onClick={() => exportFile(exportCsv, 'contacts.csv')} disabled={isExporting}><Download className="mr-2 h-4 w-4" />Export</Button><Button onClick={() => navigate('/contacts/new')}><Plus className="mr-2 h-4 w-4" />Add Contact</Button></>}>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search contacts..." />
      <DataTable columns={columns} data={contacts} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} onRowClick={(row) => navigate(`/contacts/${row.id}`)} />
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete {deleteTarget ? formatFullName(deleteTarget.firstName, deleteTarget.lastName) : ''}? This action cannot be undone.
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox id="deleteDeals" checked={deleteDeals} onCheckedChange={(checked) => setDeleteDeals(!!checked)} />
                <Label htmlFor="deleteDeals" className="text-sm font-normal">Also delete associated deals</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="deleteLead" checked={deleteLead} onCheckedChange={(checked) => setDeleteLead(!!checked)} />
                <Label htmlFor="deleteLead" className="text-sm font-normal">Also delete original lead</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => { if (deleteTarget) { deleteContact.mutate({ id: deleteTarget.id, deleteDeals, deleteLead }); setDeleteTarget(null); } }} disabled={deleteContact.isPending}>
              {deleteContact.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
